<template>
  <div id="dialog-schedule-maker">
    <span class="cls" @click="close"></span>
    <div class="paper">
      
      <!-- 헤더 -->
      <div class="header">
        <p>📋 오늘의 일정 만들기</p>
        <p @click="close"><i class="ph-bold ph-x"></i></p>
      </div>

      <!-- 스텝 인디케이터 (클릭으로 이동 가능) -->
      <div class="step-indicator">
        <div 
          v-for="(label, i) in stepLabels" 
          :key="i"
          class="step"
          :class="{ 
            active: step === i + 1, 
            done: step > i + 1,
            clickable: canGoToStep(i + 1)
          }"
          @click="goToStep(i + 1)"
        >
          <span class="step-num">{{ step > i + 1 ? '✓' : i + 1 }}</span>
          <span class="step-label">{{ label }}</span>
        </div>
      </div>

      <div class="content">
        <!-- 스텝 1: 브레인덤핑 -->
        <div class="step-content" v-if="step === 1">
          <p class="step-desc">오늘 해야 할 일, 하고 싶은 일을 마구 쏟아내세요! 🧠</p>
          
          <div class="dump-input-group">
            <input
              v-model="newTask"
              @keypress.enter="addTask"
              type="text"
              placeholder="할 일을 입력하고 Enter..."
              class="dump-input"
              ref="dumpInput"
            />
            <button @click="addTask" class="add-btn">+</button>
          </div>

          <!-- 빠른 추가 제안 -->
          <div class="quick-suggest" v-if="tasks.length === 0">
            <p class="suggest-label">💡 빠른 추가</p>
            <div class="suggest-chips">
              <span 
                v-for="s in quickSuggestions" 
                :key="s" 
                class="suggest-chip"
                @click="addQuickTask(s)"
              >{{ s }}</span>
            </div>
          </div>

          <div class="task-list" ref="taskListRef">
            <div 
              v-for="(task, index) in tasks" 
              :key="task.id"
              class="task-item"
              :class="{ 'drag-over': dragOverIndex === index }"
              draggable="true"
              @dragstart="onDragStart(index)"
              @dragover.prevent="onDragOver(index)"
              @dragleave="onDragLeave"
              @drop="onDrop(index)"
              @dragend="onDragEnd"
            >
              <span class="drag-handle" title="드래그하여 순서 변경">⠿</span>
              
              <!-- 인라인 수정 모드 -->
              <template v-if="editingIndex === index">
                <input
                  v-model="editingTitle"
                  @keypress.enter="saveEdit(index)"
                  @keydown.esc="cancelEdit"
                  @blur="saveEdit(index)"
                  class="edit-input"
                  ref="editInput"
                />
                <button @click="saveEdit(index)" class="edit-action save-btn" title="저장">✓</button>
                <button @click="cancelEdit" class="edit-action cancel-btn" title="취소">✕</button>
              </template>
              
              <!-- 일반 표시 모드 -->
              <template v-else>
                <span class="task-text" @dblclick="startEdit(index)">{{ task.title }}</span>
                <button @click="startEdit(index)" class="icon-btn edit-btn" title="수정">
                  <i class="ph ph-pencil-simple"></i>
                </button>
                <button @click="removeTask(index)" class="icon-btn remove-btn" title="삭제">✕</button>
              </template>
            </div>
            <p v-if="tasks.length === 0" class="empty-hint">
              💡 생각나는 대로 적어보세요!<br>
              <small>더블클릭으로 수정, 드래그로 순서 변경</small>
            </p>
          </div>

          <div class="task-count">{{ tasks.length }}개의 할 일</div>
        </div>

        <!-- 스텝 2: 우선순위 선택 -->
        <div class="step-content" v-if="step === 2">
          <p class="step-desc">오늘 꼭 끝내야 하는 일을 선택하세요! 🎯<br>
            <small class="step-sub-desc">최대 3개까지 선택할 수 있어요</small>
          </p>
          
          <div class="select-count">
            <span :class="{ complete: selectedTasks.length >= 1 }">
              {{ selectedTasks.length }} / 3 선택됨
              <template v-if="selectedTasks.length >= 1 && selectedTasks.length < 3">
                · 진행 가능 ✓
              </template>
            </span>
          </div>

          <div class="task-list selectable">
            <div 
              v-for="(task) in tasks" 
              :key="task.id"
              class="task-item"
              :class="{ 
                selected: isSelected(task),
                disabled: selectedTasks.length >= 3 && !isSelected(task)
              }"
              @click="toggleSelect(task)"
            >
              <span class="select-indicator">
                {{ isSelected(task) ? '✓' : '' }}
              </span>
              <span class="task-text">{{ task.title }}</span>
              <span class="priority-badge" v-if="isSelected(task)">
                TOP {{ getSelectedIndex(task) + 1 }}
              </span>
            </div>
          </div>

          <!-- 선택 안 된 나머지 항목 안내 -->
          <div class="other-tasks-note" v-if="selectedTasks.length > 0 && tasks.length > selectedTasks.length">
            <small>💡 선택되지 않은 {{ tasks.length - selectedTasks.length }}개는 일반 할일로 저장돼요</small>
          </div>
        </div>

        <!-- 스텝 3: 시간 배치 -->
        <div class="step-content step-3" v-if="step === 3">
          <div class="timeline-layout">
            <!-- 왼쪽: 배치할 Task 목록 -->
            <div class="task-panel">
              <p class="panel-title">📋 배치할 일정</p>
              <div 
                v-for="(task, index) in selectedTasks" 
                :key="task.id"
                class="task-chip"
                :class="{ 
                  active: currentTask === task,
                  placed: task.time 
                }"
                @click="selectTask(task)"
              >
                <span class="chip-order">{{ index + 1 }}</span>
                <span class="chip-title">{{ task.title }}</span>
                <span class="chip-time" v-if="task.time">{{ task.time }}</span>
                <span class="chip-clear" v-if="task.time" @click.stop="removeTaskTime(task)" title="시간 해제">✕</span>
                <select 
                  v-model="task.duration" 
                  class="duration-select"
                  @click.stop
                >
                  <option :value="10">10분</option>
                  <option :value="15">15분</option>
                  <option :value="30">30분</option>
                  <option :value="45">45분</option>
                  <option :value="60">1시간</option>
                  <option :value="90">1시간 30분</option>
                  <option :value="120">2시간</option>
                  <option :value="180">3시간</option>
                </select>
              </div>

              <!-- 배치 진행 상태 -->
              <div class="placement-status">
                <div class="status-bar">
                  <div class="status-fill" :style="{ width: placementPercent + '%' }"></div>
                </div>
                <small>{{ placedCount }} / {{ selectedTasks.length }} 배치 완료</small>
              </div>

              <p class="hint" v-if="!currentTask">👆 일정을 선택 후 타임라인 클릭</p>
              <p class="hint" v-else-if="currentTask.time">👆 다른 시간대를 클릭하면 이동</p>
              <p class="hint" v-else>👉 오른쪽 타임라인에서 시간대 클릭</p>
            </div>

            <!-- 오른쪽: 타임라인 -->
            <div class="timeline-panel" ref="timelinePanel">
              <div class="timeline-grid">
                <div 
                  v-for="hour in timeSlots" 
                  :key="hour"
                  class="time-row"
                  :class="{ 'current-hour': isCurrentHour(hour) }"
                >
                  <span class="time-label">{{ formatHour(hour) }}</span>
                  <div class="time-slots">
                    <div 
                      v-for="min in [0, 30]" 
                      :key="min"
                      class="slot"
                      :class="{ 
                        'half': min === 30,
                        'clickable': currentTask,
                        'occupied': isSlotOccupied(hour, min) && !getTaskAt(hour, min)
                      }"
                      @click="placeTask(hour, min)"
                    >
                      <!-- 현재 시간 표시선 -->
                      <div 
                        v-if="isCurrentTimeSlot(hour, min)"
                        class="current-time-line"
                        :style="{ top: currentTimeOffset + 'px' }"
                      >
                        <span class="current-time-dot"></span>
                      </div>

                      <!-- 배치된 Task 표시 -->
                      <div 
                        v-if="getTaskAt(hour, min)"
                        class="placed-task"
                        :style="{ height: getTaskHeight(getTaskAt(hour, min)) + 'px' }"
                        :class="{ 'is-active': currentTask === getTaskAt(hour, min) }"
                        @click.stop="onPlacedTaskClick(getTaskAt(hour, min))"
                      >
                        <span class="placed-title">{{ getTaskAt(hour, min).title }}</span>
                        <span class="placed-duration">{{ formatDuration(getTaskAt(hour, min).duration) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 푸터 버튼 -->
      <div class="footer">
        <button 
          v-if="step > 1" 
          @click="prevStep" 
          class="btn-secondary"
        >
          ← 이전
        </button>
        <div class="spacer"></div>

        <!-- 스텝 2에서 "전체 선택" 버튼 -->
        <button
          v-if="step === 2 && tasks.length <= 3 && selectedTasks.length !== tasks.length"
          @click="selectAll"
          class="btn-secondary"
        >
          전체 선택
        </button>

        <!-- 3개 이하면 스킵 가능 -->
        <button
          v-if="step === 1 && tasks.length > 0 && tasks.length <= 3"
          @click="skipToStep3"
          class="btn-tertiary"
        >
          바로 시간 배치 →
        </button>

        <button 
          v-if="step < 3" 
          @click="nextStep" 
          class="btn-primary"
          :disabled="!canProceed"
        >
          다음 →
        </button>
        <button 
          v-if="step === 3" 
          @click="complete" 
          class="btn-success"
          :disabled="!canComplete || saving"
        >
          {{ saving ? '저장중...' : '✓ 완료' }}
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick, inject, watch } from 'vue';

export default {
  name: 'ScheduleMakerDialog',
  props: {
    oth_showScheduleMaker: Function
  },
  setup(props) {
    const getMbid = inject('getMbid');
    const sql_query = inject('sql_query');

    const step = ref(1);
    const newTask = ref('');
    const tasks = ref([]);
    const selectedTasks = ref([]);
    const dumpInput = ref(null);
    const saving = ref(false);
    let taskIdCounter = 0;

    // 인라인 수정
    const editingIndex = ref(-1);
    const editingTitle = ref('');
    const editInput = ref(null);

    // 드래그 정렬
    const dragIndex = ref(-1);
    const dragOverIndex = ref(-1);

    // 타임라인 refs
    const timelinePanel = ref(null);
    const taskListRef = ref(null);

    const stepLabels = ['브레인덤핑', '우선순위', '시간 배치'];

    const quickSuggestions = [
      '미팅 준비', '고객 상담', '보고서 작성', '이메일 확인',
      '운동', '점심 약속', '자료 정리', '공부'
    ];

    function close() {
      props.oth_showScheduleMaker(false);
    }

    onMounted(() => {
      dumpInput.value?.focus();
    });

    // ========== 브레인덤핑 ==========
    function generateId() {
      return `task_${++taskIdCounter}_${Date.now()}`;
    }

    function addTask() {
      const title = newTask.value.trim();
      if (!title) return;
      tasks.value.push({ id: generateId(), title, time: '', duration: 30 });
      newTask.value = '';
      nextTick(() => {
        dumpInput.value?.focus();
      });
    }

    function addQuickTask(title) {
      tasks.value.push({ id: generateId(), title, time: '', duration: 30 });
      nextTick(() => {
        dumpInput.value?.focus();
      });
    }

    function removeTask(index) {
      const removed = tasks.value.splice(index, 1)[0];
      selectedTasks.value = selectedTasks.value.filter(t => t !== removed);
    }

    // 인라인 수정
    function startEdit(index) {
      editingIndex.value = index;
      editingTitle.value = tasks.value[index].title;
      nextTick(() => {
        const inputs = document.querySelectorAll('.edit-input');
        if (inputs.length > 0) inputs[0].focus();
      });
    }

    function saveEdit(index) {
      const trimmed = editingTitle.value.trim();
      if (trimmed && editingIndex.value === index) {
        tasks.value[index].title = trimmed;
      }
      editingIndex.value = -1;
      editingTitle.value = '';
    }

    function cancelEdit() {
      editingIndex.value = -1;
      editingTitle.value = '';
    }

    // 드래그 정렬
    function onDragStart(index) {
      dragIndex.value = index;
    }

    function onDragOver(index) {
      if (dragIndex.value === -1) return;
      dragOverIndex.value = index;
    }

    function onDragLeave() {
      dragOverIndex.value = -1;
    }

    function onDrop(index) {
      if (dragIndex.value === -1 || dragIndex.value === index) {
        dragOverIndex.value = -1;
        return;
      }
      const item = tasks.value.splice(dragIndex.value, 1)[0];
      tasks.value.splice(index, 0, item);
      dragOverIndex.value = -1;
      dragIndex.value = -1;
    }

    function onDragEnd() {
      dragIndex.value = -1;
      dragOverIndex.value = -1;
    }

    // ========== TOP 선택 ==========
    function isSelected(task) {
      return selectedTasks.value.includes(task);
    }

    function getSelectedIndex(task) {
      return selectedTasks.value.indexOf(task);
    }

    function toggleSelect(task) {
      if (isSelected(task)) {
        selectedTasks.value = selectedTasks.value.filter(t => t !== task);
      } else if (selectedTasks.value.length < 3) {
        if (!task.duration) task.duration = 30;
        selectedTasks.value.push(task);
      }
    }

    function selectAll() {
      selectedTasks.value = [];
      tasks.value.forEach(t => {
        if (selectedTasks.value.length < 3) {
          if (!t.duration) t.duration = 30;
          selectedTasks.value.push(t);
        }
      });
    }

    // 3개 이하일 때 스텝2 건너뛰기
    function skipToStep3() {
      selectedTasks.value = [];
      tasks.value.forEach(t => {
        if (!t.duration) t.duration = 30;
        selectedTasks.value.push(t);
      });
      step.value = 3;
      nextTick(() => scrollToCurrentHour());
    }

    // ========== 타임라인 ==========
    const currentTask = ref(null);
    
    const timeSlots = computed(() => {
      const slots = [];
      for (let h = 6; h <= 23; h++) slots.push(h);
      return slots;
    });

    function formatHour(hour) {
      return `${String(hour).padStart(2, '0')}:00`;
    }

    function formatDuration(min) {
      if (min < 60) return `${min}분`;
      const h = Math.floor(min / 60);
      const m = min % 60;
      return m > 0 ? `${h}시간 ${m}분` : `${h}시간`;
    }

    function selectTask(task) {
      currentTask.value = task;
    }

    function placeTask(hour, min) {
      if (!currentTask.value) return;
      
      const timeStr = `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
      
      // 이미 배치된 task도 시간 변경 가능
      currentTask.value.time = timeStr;
      currentTask.value = null;
    }

    function getTaskAt(hour, min) {
      const timeStr = `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
      return selectedTasks.value.find(t => t.time === timeStr);
    }

    // 슬롯이 다른 task의 duration 범위에 있는지 확인
    function isSlotOccupied(hour, min) {
      const slotMinutes = hour * 60 + min;
      return selectedTasks.value.some(t => {
        if (!t.time) return false;
        const [h, m] = t.time.split(':').map(Number);
        const start = h * 60 + m;
        const end = start + (t.duration || 30);
        return slotMinutes >= start && slotMinutes < end;
      });
    }

    function getTaskHeight(task) {
      const baseHeight = 40;
      return (task.duration / 30) * baseHeight;
    }

    function removeTaskTime(task) {
      task.time = '';
      currentTask.value = null;
    }

    function onPlacedTaskClick(task) {
      // 이미 선택된 task를 다시 클릭하면 선택 해제
      if (currentTask.value === task) {
        currentTask.value = null;
      } else {
        currentTask.value = task;
      }
    }

    // 현재 시간 관련
    function isCurrentHour(hour) {
      return new Date().getHours() === hour;
    }

    function isCurrentTimeSlot(hour, min) {
      const now = new Date();
      return now.getHours() === hour && (min === 0 ? now.getMinutes() < 30 : now.getMinutes() >= 30);
    }

    const currentTimeOffset = computed(() => {
      const now = new Date();
      const m = now.getMinutes() % 30;
      return (m / 30) * 40;
    });

    function scrollToCurrentHour() {
      nextTick(() => {
        if (!timelinePanel.value) return;
        const hour = new Date().getHours();
        const rowIndex = Math.max(0, hour - 6 - 1);
        const scrollTop = rowIndex * 80;
        timelinePanel.value.scrollTop = scrollTop;
      });
    }

    const placedCount = computed(() => selectedTasks.value.filter(t => t.time).length);
    const placementPercent = computed(() => {
      if (selectedTasks.value.length === 0) return 0;
      return (placedCount.value / selectedTasks.value.length) * 100;
    });

    // 시간순 정렬
    const sortedSchedule = computed(() => {
      return [...selectedTasks.value].sort((a, b) => {
        if (!a.time) return 1;
        if (!b.time) return -1;
        return a.time.localeCompare(b.time);
      });
    });

    // ========== 스텝 네비게이션 ==========
    const canProceed = computed(() => {
      if (step.value === 1) return tasks.value.length > 0;
      if (step.value === 2) return selectedTasks.value.length >= 1; // 1개 이상이면 OK
      return true;
    });

    const canComplete = computed(() => {
      return selectedTasks.value.every(t => t.time);
    });

    function canGoToStep(targetStep) {
      if (targetStep <= step.value) return true;
      if (targetStep === 2) return tasks.value.length > 0;
      if (targetStep === 3) return selectedTasks.value.length >= 1;
      return false;
    }

    function goToStep(targetStep) {
      if (!canGoToStep(targetStep)) return;
      step.value = targetStep;
      if (targetStep === 3) nextTick(() => scrollToCurrentHour());
    }

    function prevStep() {
      if (step.value > 1) step.value--;
    }

    function nextStep() {
      if (!canProceed.value || step.value >= 3) return;
      step.value++;
      if (step.value === 3) nextTick(() => scrollToCurrentHour());
    }

    // step 3 진입 시 현재 시간으로 스크롤
    watch(step, (v) => {
      if (v === 3) nextTick(() => scrollToCurrentHour());
      if (v === 1) nextTick(() => dumpInput.value?.focus());
    });

    // ========== 저장 ==========
    async function complete() {
      if (!canComplete.value || saving.value) return;
      
      saving.value = true;
      
      try {
        const today = new Date();
        const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        const nowStr = `${dateStr} ${String(today.getHours()).padStart(2, '0')}:${String(today.getMinutes()).padStart(2, '0')}:${String(today.getSeconds()).padStart(2, '0')}`;
        
        // 선택된 task
        for (let i = 0; i < sortedSchedule.value.length; i++) {
          const task = sortedSchedule.value[i];
          const dueDate = `${dateStr} ${task.time}:00`;
          const priority = selectedTasks.value.indexOf(tasks.value.find(t => t.id === task.id)) + 1;
          
          await sql_query(`
            INSERT INTO todos (mb_id, title, is_completed, due_date, priority, created_at, updated_at)
            VALUES ('${getMbid()}', '${task.title.replace(/'/g, "''")}', 0, '${dueDate}', ${priority}, '${nowStr}', '${nowStr}')
          `);
        }
        
        // 나머지 브레인덤프
        const otherTasks = tasks.value.filter(t => !selectedTasks.value.includes(t));
        for (const task of otherTasks) {
          const dueDate = `${dateStr} 09:00:00`;
          await sql_query(`
            INSERT INTO todos (mb_id, title, is_completed, due_date, priority, created_at, updated_at)
            VALUES ('${getMbid()}', '${task.title.replace(/'/g, "''")}', 0, '${dueDate}', 0, '${nowStr}', '${nowStr}')
          `);
        }
        
        window.dispatchEvent(new CustomEvent('todos-updated'));
        close();
      } catch (e) {
        console.error('저장 실패:', e);
        alert('저장에 실패했습니다.');
      } finally {
        saving.value = false;
      }
    }

    return {
      step,
      stepLabels,
      newTask,
      tasks,
      selectedTasks,
      dumpInput,
      saving,
      close,
      addTask,
      addQuickTask,
      quickSuggestions,
      removeTask,
      // 수정
      editingIndex,
      editingTitle,
      editInput,
      startEdit,
      saveEdit,
      cancelEdit,
      // 드래그
      dragOverIndex,
      onDragStart,
      onDragOver,
      onDragLeave,
      onDrop,
      onDragEnd,
      taskListRef,
      // 선택
      isSelected,
      getSelectedIndex,
      toggleSelect,
      selectAll,
      skipToStep3,
      // 타임라인
      currentTask,
      timeSlots,
      formatHour,
      formatDuration,
      selectTask,
      placeTask,
      getTaskAt,
      isSlotOccupied,
      getTaskHeight,
      removeTaskTime,
      onPlacedTaskClick,
      isCurrentHour,
      isCurrentTimeSlot,
      currentTimeOffset,
      timelinePanel,
      placedCount,
      placementPercent,
      // 네비게이션
      sortedSchedule,
      canProceed,
      canComplete,
      canGoToStep,
      goToStep,
      prevStep,
      nextStep,
      complete,
    };
  }
};
</script>

<style scoped>
#dialog-schedule-maker {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  background: var(--color-surface);
  backdrop-filter: blur(var(--effect-blur));
}

.cls {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  z-index: -1;
}

.paper {
  width: 100%;
  max-width: 520px;
  height: 100%;
  max-height: 90%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background-color: var(--color-background);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
}

/* 헤더 */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 60px;
  padding: 0 24px;
  box-sizing: border-box;
  background: var(--color-background);
  border-bottom: 1px solid var(--color-text-third);
}

.header > p {
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-primary);
  font-size: var(--font-size-lg);
  cursor: pointer;
  margin: 0;
  padding: 0;
  font-weight: var(--font-weight-bold);
}

.header > p:last-child {
  font-size: var(--font-size-xl);
  font-weight: normal;
  opacity: 0.6;
}

/* 스텝 인디케이터 */
.step-indicator {
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 16px 20px;
  background: var(--color-background-light);
  border-bottom: 1px solid var(--color-text-third);
}

.step {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 20px;
  background: var(--color-surface);
  transition: all 0.3s;
}

.step.clickable {
  cursor: pointer;
}

.step.clickable:hover:not(.active):not(.done) {
  background: var(--color-text-third);
}

.step.active {
  background: #3b82f6;
}

.step.done {
  background: #22c55e;
  cursor: pointer;
}

.step-num {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  font-size: 12px;
  font-weight: bold;
  color: var(--color-text-primary);
}

.step.active .step-num,
.step.done .step-num {
  background: rgba(255, 255, 255, 0.3);
  color: #fff;
}

.step-label {
  font-size: 12px;
  color: var(--color-text-primary);
  opacity: 0.7;
}

.step.active .step-label,
.step.done .step-label {
  opacity: 1;
  color: #fff;
}

/* 컨텐츠 */
.content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.step-content {
  flex: 1;
  padding: 20px 24px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.step-desc {
  margin: 0;
  font-size: var(--font-size-md);
  color: var(--color-text-primary);
  opacity: 0.8;
  text-align: center;
  line-height: 1.5;
}

.step-sub-desc {
  font-size: var(--font-size-xs);
  opacity: 0.6;
}

/* 브레인덤핑 입력 */
.dump-input-group {
  display: flex;
  gap: 8px;
}

.dump-input {
  flex: 1;
  padding: 12px 16px;
  border: 1px solid var(--color-text-third);
  background: var(--color-background);
  color: var(--color-text-primary);
  border-radius: 10px;
  font-size: var(--font-size-md);
  transition: all 0.2s;
}

.dump-input:focus {
  outline: none;
  border-color: var(--color-brand);
}

.add-btn {
  width: 48px;
  border: none;
  background: var(--color-brand);
  color: var(--color-text-white);
  border-radius: 10px;
  cursor: pointer;
  font-size: 20px;
  font-weight: bold;
  transition: all 0.2s;
}

.add-btn:hover {
  opacity: 0.9;
}

/* 빠른 추가 제안 */
.quick-suggest {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.suggest-label {
  margin: 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.suggest-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.suggest-chip {
  padding: 6px 12px;
  background: var(--color-surface);
  border: 1px solid var(--color-text-third);
  border-radius: 16px;
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.2s;
}

.suggest-chip:hover {
  border-color: var(--color-brand);
  color: var(--color-brand);
  background: rgba(59, 130, 246, 0.05);
}

/* 태스크 리스트 */
.task-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  min-height: 150px;
}

.task-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: var(--color-background-light);
  border: 1px solid var(--color-text-third);
  border-radius: 10px;
  transition: all 0.2s;
}

.task-item.drag-over {
  border-color: var(--color-brand);
  border-style: dashed;
  background: rgba(59, 130, 246, 0.05);
}

.drag-handle {
  cursor: grab;
  color: var(--color-text-secondary);
  opacity: 0.4;
  font-size: 14px;
  user-select: none;
  flex-shrink: 0;
  transition: opacity 0.2s;
}

.drag-handle:hover {
  opacity: 0.8;
}

.task-item:active .drag-handle {
  cursor: grabbing;
}

/* 인라인 수정 */
.edit-input {
  flex: 1;
  padding: 6px 10px;
  border: 1px solid var(--color-brand);
  background: var(--color-background);
  color: var(--color-text-primary);
  border-radius: 6px;
  font-size: var(--font-size-md);
  outline: none;
}

.edit-action {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  flex-shrink: 0;
}

.save-btn {
  background: #22c55e;
  color: white;
}

.cancel-btn {
  background: var(--color-surface);
  color: var(--color-text-secondary);
}

/* 아이콘 버튼 */
.icon-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 14px;
  opacity: 0;
  transition: all 0.15s;
  flex-shrink: 0;
}

.task-item:hover .icon-btn {
  opacity: 0.5;
}

.icon-btn:hover {
  opacity: 1 !important;
  background: var(--color-surface);
}

.edit-btn {
  color: var(--color-text-secondary);
}

.remove-btn {
  color: var(--color-special-warning);
}

.task-list.selectable .task-item {
  cursor: pointer;
}

.task-list.selectable .task-item:hover:not(.disabled) {
  border-color: var(--color-brand);
}

.task-list.selectable .task-item.selected {
  background: rgba(34, 197, 94, 0.15);
  border-color: #22c55e;
}

.task-list.selectable .task-item.disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.select-indicator {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--color-text-third);
  border-radius: 6px;
  font-size: 14px;
  color: #22c55e;
  flex-shrink: 0;
}

.task-item.selected .select-indicator {
  background: #22c55e;
  border-color: #22c55e;
  color: white;
}

.task-text {
  flex: 1;
  color: var(--color-text-primary);
  font-size: var(--font-size-md);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.priority-badge {
  padding: 4px 10px;
  background: #f59e0b;
  color: #000;
  border-radius: 12px;
  font-size: 11px;
  font-weight: bold;
  flex-shrink: 0;
}

.empty-hint {
  text-align: center;
  color: var(--color-text-primary);
  opacity: 0.4;
  padding: 40px 20px;
  font-size: var(--font-size-md);
  line-height: 1.6;
}

.empty-hint small {
  font-size: var(--font-size-xs);
  opacity: 0.7;
}

.task-count {
  text-align: center;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.select-count {
  text-align: center;
}

.select-count span {
  display: inline-block;
  padding: 6px 16px;
  background: var(--color-surface);
  border-radius: 20px;
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  transition: all 0.3s;
}

.select-count span.complete {
  background: rgba(34, 197, 94, 0.2);
  color: #22c55e;
}

.other-tasks-note {
  text-align: center;
  padding: 8px;
  background: rgba(59, 130, 246, 0.05);
  border-radius: 8px;
  color: var(--color-text-secondary);
}

/* ========== Step 3: 타임라인 레이아웃 ========== */
.step-3 {
  padding: 0 !important;
}

.timeline-layout {
  display: flex;
  height: 100%;
  overflow: hidden;
}

/* 왼쪽 패널 */
.task-panel {
  width: 200px;
  min-width: 200px;
  padding: 16px;
  border-right: 1px solid var(--color-text-third);
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
}

.panel-title {
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: 600;
  color: var(--color-text-primary);
}

.task-chip {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: var(--color-background-light);
  border: 2px solid var(--color-text-third);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.task-chip:hover {
  border-color: var(--color-brand);
}

.task-chip.active {
  border-color: var(--color-brand);
  background: rgba(59, 130, 246, 0.1);
}

.task-chip.placed {
  border-color: #22c55e;
  background: rgba(34, 197, 94, 0.1);
}

.chip-order {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-brand);
  color: #fff;
  border-radius: 50%;
  font-size: 11px;
  font-weight: bold;
}

.task-chip.placed .chip-order {
  background: #22c55e;
}

.chip-title {
  flex: 1;
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chip-time {
  font-size: var(--font-size-xs);
  color: #22c55e;
  font-weight: 600;
}

.chip-clear {
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
  font-size: 10px;
  cursor: pointer;
  transition: all 0.15s;
}

.chip-clear:hover {
  background: #ef4444;
  color: white;
}

.duration-select {
  width: 100%;
  margin-top: 4px;
  padding: 6px 8px;
  border: 1px solid var(--color-text-third);
  border-radius: 6px;
  background: var(--color-background);
  color: var(--color-text-primary);
  font-size: var(--font-size-xs);
  cursor: pointer;
}

/* 배치 진행 상태 */
.placement-status {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 0;
}

.placement-status small {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  text-align: center;
}

.status-bar {
  height: 4px;
  background: var(--color-surface);
  border-radius: 2px;
  overflow: hidden;
}

.status-fill {
  height: 100%;
  background: #22c55e;
  border-radius: 2px;
  transition: width 0.3s ease;
}

.task-panel .hint {
  margin: 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  text-align: center;
  padding: 8px;
}

/* 오른쪽 타임라인 패널 */
.timeline-panel {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.timeline-grid {
  display: flex;
  flex-direction: column;
}

.time-row {
  display: flex;
  min-height: 80px;
  border-bottom: 1px solid var(--color-text-third);
  transition: background 0.2s;
}

.time-row.current-hour {
  background: rgba(59, 130, 246, 0.03);
}

.time-label {
  width: 50px;
  min-width: 50px;
  padding: 4px 8px;
  font-size: var(--font-size-xs);
  font-weight: 600;
  color: var(--color-text-secondary);
}

.time-row.current-hour .time-label {
  color: #3b82f6;
}

.time-slots {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.slot {
  flex: 1;
  min-height: 40px;
  border-left: 1px solid var(--color-text-third);
  position: relative;
  transition: background 0.15s;
}

.slot.half {
  border-top: 1px dashed var(--color-text-third);
}

.slot.clickable {
  cursor: pointer;
}

.slot.clickable:hover {
  background: rgba(59, 130, 246, 0.1);
}

.slot.occupied {
  background: rgba(59, 130, 246, 0.03);
}

/* 현재 시간 표시선 */
.current-time-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  background: #ef4444;
  z-index: 5;
  pointer-events: none;
}

.current-time-dot {
  position: absolute;
  left: -4px;
  top: -3px;
  width: 8px;
  height: 8px;
  background: #ef4444;
  border-radius: 50%;
}

/* 배치된 Task */
.placed-task {
  position: absolute;
  left: 4px;
  right: 4px;
  top: 2px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  border-radius: 6px;
  padding: 6px 8px;
  color: #fff;
  cursor: pointer;
  overflow: hidden;
  z-index: 10;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  transition: transform 0.15s, box-shadow 0.15s;
}

.placed-task:hover {
  transform: scale(1.02);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.placed-task.is-active {
  box-shadow: 0 0 0 2px #fff, 0 0 0 4px #3b82f6;
}

.placed-title {
  font-size: var(--font-size-xs);
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.placed-duration {
  font-size: 10px;
  opacity: 0.8;
}

/* 푸터 */
.footer {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid var(--color-text-third);
}

.spacer {
  flex: 1;
}

.btn-secondary,
.btn-primary,
.btn-success,
.btn-tertiary {
  padding: 10px 20px;
  border: none;
  border-radius: 10px;
  font-size: var(--font-size-sm);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary {
  background: var(--color-surface);
  color: var(--color-text-primary);
}

.btn-primary {
  background: var(--color-brand);
  color: var(--color-text-white);
}

.btn-success {
  background: #22c55e;
  color: white;
}

.btn-tertiary {
  background: transparent;
  color: var(--color-text-secondary);
  border: 1px dashed var(--color-text-third);
}

.btn-tertiary:hover {
  border-color: var(--color-brand);
  color: var(--color-brand);
}

.btn-secondary:hover,
.btn-primary:hover,
.btn-success:hover {
  opacity: 0.9;
}

.btn-primary:disabled,
.btn-success:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* 스크롤바 */
.step-content::-webkit-scrollbar,
.task-panel::-webkit-scrollbar,
.timeline-panel::-webkit-scrollbar {
  width: 6px;
}

.step-content::-webkit-scrollbar-track,
.task-panel::-webkit-scrollbar-track,
.timeline-panel::-webkit-scrollbar-track {
  background: transparent;
}

.step-content::-webkit-scrollbar-thumb,
.task-panel::-webkit-scrollbar-thumb,
.timeline-panel::-webkit-scrollbar-thumb {
  background: var(--color-text-third);
  border-radius: 3px;
}

/* 모바일 */
@media (max-width: 480px) {
  .paper {
    max-width: 100%;
    max-height: 100%;
    border-radius: 0;
  }

  .step-indicator {
    gap: 4px;
    padding: 12px;
  }

  .step {
    padding: 6px 10px;
  }

  .step-label {
    font-size: 11px;
  }

  .step-content {
    padding: 16px;
  }

  .drag-handle {
    display: none;
  }

  .icon-btn {
    opacity: 0.5;
  }

  /* 타임라인 모바일 */
  .timeline-layout {
    flex-direction: column;
  }

  .task-panel {
    width: 100%;
    min-width: 100%;
    border-right: none;
    border-bottom: 1px solid var(--color-text-third);
    max-height: 180px;
  }

  .task-chip {
    padding: 8px 10px;
  }

  .timeline-panel {
    padding: 12px;
  }

  .time-label {
    width: 40px;
    min-width: 40px;
    font-size: 10px;
  }

  .suggest-chips {
    gap: 4px;
  }

  .suggest-chip {
    padding: 5px 10px;
    font-size: 11px;
  }
}
</style>